import { defineComponent as _defineComponent } from 'vue'
import ChangeStoreSideMenu from 'JS/components/side-menus/change-store/ChangeStoreSideMenu.vue';
import MobileMenuHeader from './MobileMenuHeader.vue';
import MobileMenuItem from './MobileMenuItem.vue';
import VIntersectionObserver from 'JS/components/VIntersectionObserver.vue';
import { computed, nextTick, onMounted, onUnmounted, ref } from 'vue';
import { IBannerXAd } from 'JS/sponsored/sponsored-types';
import { IHeaderJson, MenuLinkType } from 'JS/types/Header';
import { setMenuOpenClasses, unsetOpenClasses } from '../../util/menu.utils';


export default /*#__PURE__*/_defineComponent({
  __name: 'MobileMenu',
  props: {
    headerData: null,
    megaMenuAd: null
  },
  emits: ["close", "get-mega-menu-ad", "record-ad-impression"],
  setup(__props: any, { emit }: { emit: ({
	(event: 'close'): void;
	(event: 'get-mega-menu-ad'): void;
	(event: 'record-ad-impression'): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as {
	headerData: IHeaderJson,
	megaMenuAd?: IBannerXAd,
}





const currentMenu = ref(null);
const menuHistory = ref([]);

const setStoreMenuOpen = ref(false);

const accessibleText = computed<string>(() => {
	const sponsored = "Sponsored ";
	if (props.megaMenuAd?.bannerImageAccessibleText === props.megaMenuAd?.ctaAccessibleText) {
		return sponsored + props.megaMenuAd?.bannerImageAccessibleText;
	} else {
		return sponsored + props.megaMenuAd?.bannerImageAccessibleText + " " + props.megaMenuAd?.ctaAccessibleText;
	}
});

const headerText = computed(() => {
	if (currentMenu.value) return currentMenu.value.name;
	return props.headerData.localStoreJson ? 'Your Store' : null;
});

const animalRxMenu = computed(() => {
	return props.headerData.deptMenu.find(d => d.id === "13");
});

const deptMenu = computed(() => {
	return props.headerData.deptMenu.filter(d => d.id !== "13" && d.id !== "12");
});

const mainLinksPrimary = computed(() => {
	const primaryLinks = [
		{ id: 12, name: 'All Departments', action: 'deptMenu', url: undefined, class: 'primary', hierLevel: 0, linkType: 'category' },
	];

	let nextId = 30; //30 is picked arbitrarily as a number high enough to ignore any other ID's seen below
	for (const link of props.headerData.headerLinks) {
		primaryLinks.push({ id: nextId++, name: link.text, action: undefined, url: link.link, class: 'primary', hierLevel: 0, linkType: 'featured' });
	}

	return primaryLinks;
});

const mainLinksSecondary = computed(() => {
	return [
		{ id: 20, name: 'Automotive Service Center', class: 'secondary', hierLevel: 0, children: [
			{ parentID: 20, name: 'Tires', url: '/tires/', hierLevel: 0 },
			{ parentID: 20, name: 'Batteries', url: '/batteries/', hierLevel: 0 },
			{ parentID: 20, name: 'Services & Repair', url: '/automotive-service-repair/', hierLevel: 0 },
		]
		},
		{ id: 6, name: 'Gift Cards', url: '/gift-cards/', class: 'secondary', hierLevel: 0 },
		{ id: 16, name: 'Blain\'s Blog', url: '/blog/', class: 'secondary', hierLevel: 0 },
		{ id: 23, name: 'Blain\'s Events', url: '/blog/tag/events/', class: 'secondary', hierLevel: 0 },
		{ id: 14, name: 'Blain\'s Rewards', url: '/account/rewards/', action: 'rewards', class: 'secondary', hierLevel: 0 },
		{ id: 22, name: 'Blain\'s Mastercard', url: '/mastercard/', hierLevel: 0 },
		{ id: 17, name: 'Registries & Lists', url: '/gift-registry/', class: 'secondary', hierLevel: 0 },
		{ id: 13, name: 'Blain\'s Animal Rx', class: 'secondary', action: 'animalRx', hierLevel: 0 },
		{ id: 18, name: 'Store Finder', url: '/store-finder/', class: 'secondary', hierLevel: 0 },
		{ id: 9, name: 'Customer Care', hierLevel: 0, children: [
			{ parentID: 9, name: 'Contact Farm & Fleet', url: '/customer-care/', hierLevel: 0 },
			{ parentID: 9, name: 'SDS Request Form', url: '/sds-request/', hierLevel: 0 },
			{ parentID: 9, name: 'Shipping & Returns', url: '/shipping-returns/', hierLevel: 0 },
			{ parentID: 9, name: 'Product Recalls', url: '/recalls/', hierLevel: 0 },
			{ parentID: 9, name: 'Rebates', url: '/rebates/', hierLevel: 0 },
			{ parentID: 9, name: 'FAQ', url: '/faq/', hierLevel: 0 },
		] },
		{ id: 8, name: 'Services', hierLevel: 0, children: [
			{ parentID: 8, name: 'Drive-Thru Pickup', url: '/store-pickup/', hierLevel: 0 },
			{ parentID: 8, name: 'Credit Card', url: '/mastercard/', hierLevel: 0 },
			{ parentID: 8, name: 'Extended Service Program', url: '/best-program/', hierLevel: 0 },
			{ parentID: 8, name: 'Fishing & Hunting Licenses', url: '/fishing-hunting-licenses/', hierLevel: 0 },
			{ parentID: 8, name: 'Gift Registries', url: '/gift-registry/', hierLevel: 0 },
			{ parentID: 8, name: 'Other Services', url: '/other-services/', hierLevel: 0 },
		] },
		{ id: 10, name: 'Our Company', hierLevel: 0, children: [
			{ parentID: 10, name: 'About Us', url: '/about-us/', hierLevel: 0 },
			{ parentID: 10, name: 'News', url: '/blog/category/events-news/', hierLevel: 0 },
			{ parentID: 10, name: 'Careers', url: 'https://careers.farmandfleet.com/', hierLevel: 0 },
			{ parentID: 10, name: 'Modeling', url: '/modeling/', hierLevel: 0 },
			{ parentID: 10, name: 'Real Estate', url: '/real-estate/', hierLevel: 0 },
			{ parentID: 10, name: 'History', url: '/history/', hierLevel: 0 },
			{ parentID: 10, name: 'Community', url: '/community/', hierLevel: 0 },
			{ parentID: 10, name: 'Our Theme Song', url: '/theme-song/', hierLevel: 0 },
			{ parentID: 10, name: 'Legal Notice', url: '/legal-notice/', hierLevel: 0 },
			{ parentID: 10, name: 'Privacy Policy', url: '/privacy-policy/', hierLevel: 0 },
			{ parentID: 10, name: 'Coupons', url: '/coupons/', hierLevel: 0 },
			{ parentID: 10, name: 'Coupon Policy', url: '/coupon-acceptance-policy/', hierLevel: 0 }
		] }
	];
});

function goBack() {
	const oldMenu = currentMenu.value;
	let focusID = "";

	currentMenu.value = menuHistory.value[menuHistory.value.length - 1];
	menuHistory.value.pop();

	//Department menu main page
	if (currentMenu.value == null && Array.isArray(oldMenu)) {
		focusID = '12';
	} else {
		focusID = oldMenu.id;
	}

	nextTick(()=> {
		const el = document.querySelector(`.mobile-menu-item--${focusID}`) as HTMLElement;
		if (el) {
			el.focus();
		}
	});
}

function expand(menuItem) {
	if (handleAction(menuItem.action)) {
		return;
	}

	if ((!menuItem.children || menuItem.children.length < 1) && menuItem.url) {
		handleMenuClick(menuItem.hierLevel, menuItem.linkType ?? 'other');
		window.location.replace(menuItem.url);
	} else {
		menuHistory.value.push(currentMenu.value);
		currentMenu.value = menuItem;
		focusBackButton();
	}
}

function handleAction(action) {
	if (action === 'deptMenu') {
		menuHistory.value.push(currentMenu.value);
		currentMenu.value = deptMenu.value;
		focusBackButton();
		return true;
	}

	if (action === 'rewards') {
		handleMenuClick(0);
		props.headerData.loggedIn ? window.location.replace('/account/rewards/')
			: window.location.replace('/rewards/');
		return true;
	}

	if (action === 'animalRx') {
		menuHistory.value.push(currentMenu.value);
		currentMenu.value = animalRxMenu.value;
		focusBackButton();
		return true;
	}

	return false;
}

function handleMegaMenuEscape() {
	emit('close');
}

function handleMenuClick(menuLevel: number, menuLinkType: MenuLinkType = 'other') {
	window.blainGtmDataLayer.push({
		'menuName': "main",
		'menuLevel': menuLevel,
		'menuLinkType': menuLinkType,
		'event': "menu_click"
	});
}

function focusBackButton() {
	nextTick(()=> {
		const el = document.querySelector(".drawer-head__back-btn") as HTMLElement;
		if (el) {
			el.focus();
		}
	});
}

function recordAdClick() {
	navigator.sendBeacon('/event/a/sponsored-ad-click', JSON.stringify(props.megaMenuAd.adId));
}

onMounted(()=> {
	setMenuOpenClasses(true);

	emit('get-mega-menu-ad');
});

onUnmounted(() => {
	unsetOpenClasses();
});


return { __sfc: true,props, emit, currentMenu, menuHistory, setStoreMenuOpen, accessibleText, headerText, animalRxMenu, deptMenu, mainLinksPrimary, mainLinksSecondary, goBack, expand, handleAction, handleMegaMenuEscape, handleMenuClick, focusBackButton, recordAdClick, ChangeStoreSideMenu, MobileMenuHeader, MobileMenuItem, VIntersectionObserver }
}

})